import { useDuplicateFlow, useGetFlow } from "@/queries/flows/flows.api";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
  TextAreaField,
  useToast,
} from "@elaniintc/goes-design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { AxiosError } from "axios";
import Modal from "@/components/common/modal";
import { DialogFooter } from "@/components/ui/dialog";

const restoreVersionSchema = z.object({
  version_description: z
    .string()
    .min(5, "El motivo es requerido")
    .max(255, "El motivo no puede exceder 255 caracteres"),
});

type RestoreVersionFormData = z.infer<typeof restoreVersionSchema>;

interface Props {
  flowId: string;
  latestVersion: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newVersion: number) => void;
}

const RestoreVersionModal = ({
  flowId,
  latestVersion,
  isOpen,
  onClose,
  onSuccess,
}: Props) => {
  const client = useQueryClient();
  const { data: flowData, isLoading: isLoadingFlowData } = useGetFlow(
    flowId,
    false,
  );
  const { mutateAsync, isPending: isLoading } = useDuplicateFlow(flowId);
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<RestoreVersionFormData>({
    resolver: zodResolver(restoreVersionSchema),
    defaultValues: {
      version_description: "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const version_description = watch("version_description");

  const onSubmit = async (formData: RestoreVersionFormData) => {
    try {
      const newVersion = latestVersion + 1;

      await mutateAsync({
        name: flowData?.data.name || "",
        version_description: formData.version_description,
        versioning: true,
      });

      showToast({
        title: "Restauración en proceso",
        description:
          "La versión se está restaurando. Podrás ver sus detalles una vez se haya completado.",
        options: {
          type: "success",
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="info" shape="square">
                <Icon type="fas" name="clock-rotate-left" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });

      onSuccess?.(newVersion);
      onClose();
    } catch (error) {
      const e = error as AxiosError<{ message: string }>;
      setError("version_description", {
        type: "server",
        message:
          e?.response?.data?.message ||
          e?.message ||
          "Error al restaurar la versión",
      });
    }
  };

  const handleClose = () => {
    reset({ version_description: "" });
    clearErrors();
    client.invalidateQueries({
      queryKey: ["admin-flow-detail", flowId],
      exact: true,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen && !!flowData}
      onClose={handleClose}
      title="Restaurar versión"
      description="¿Estás seguro de que deseas restaurar esta versión? Se creará una nueva versión en borrador."
      modalContentClassName="max-w-[520px] w-full gap-7"
    >
      <form
        className="flex flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
        id="restore-version-form"
      >
        <TextAreaField
          label="Motivo de la restauración de versión"
          required
          maxLength={120}
          placeholder="Descripción del motivo para restaurar esta versión"
          rows={3}
          className="resize-none"
          error={errors.version_description?.message}
          {...register("version_description")}
          count={version_description?.length || 0}
          limit={120}
        />

        <DialogFooter className="pt-1">
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 gap-3">
            <Button
              size="lg"
              type="button"
              className="w-full"
              variant="tertiary"
              onClick={handleClose}
              disabled={isLoading || isLoadingFlowData}
            >
              Cancelar
            </Button>
            <Button
              size="lg"
              className="w-full"
              type="submit"
              disabled={isLoading || !isValid || isLoadingFlowData}
              isLoading={isLoading}
            >
              Restaurar versión
            </Button>
          </div>
        </DialogFooter>
      </form>
    </Modal>
  );
};

export default RestoreVersionModal;
